import {
  Alert,
  Avatar,
  Button,
  EmptyState,
  Heading,
  KeyValue,
  MoreMenu,
  Section,
  Skeleton,
  Stack,
  phoneQuery,
  useMediaQuery,
  StatCard,
} from '@ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { PageActions } from '../../../app/PageActions';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useLocalColumnSort } from '../../../lib/useColumnSort';
import { useSales, useSalesSummary } from '../../sales/api';
import { SalesTable } from '../../sales/components/SalesTable';
import { formatEuro } from '../../sales/format';
import { useMember, useMembers, type Member } from '../api';
import { useSession } from '../session';
import { useMemberActions } from './MemberActions';
import styles from './MemberDetailPage.module.css';

const LIST = '/einstellungen/mitglieder';

/**
 * One member: name, e-mail, role, member since, last activity and, with
 * `sales.read`, the sales they referred ("Vermittelt von", #26). "Rolle
 * ändern" and "Entfernen" are in the "…" menu, as in the list (#36).
 */
export function MemberDetailPage() {
  const { t } = useTranslation();
  const phone = useMediaQuery(phoneQuery);
  const { userId = '' } = useParams();
  const { can } = useSession();
  const member = useMember(userId);
  const sales = can('sales.read');

  if (member.error) {
    return (
      <EmptyState
        title={errorMessage(member.error)}
        actions={<BackLink to={LIST} label={t('members.title')} />}
      />
    );
  }
  if (!member.data) {
    return (
      <div role="status" aria-label={t('common.loading')}>
        <Skeleton lines={6} />
      </div>
    );
  }
  const m = member.data;
  return (
    <Stack gap={6}>
      <MemberMenu member={m} />
      <Stack gap={2}>
        {!phone && <BackLink to={LIST} label={t('members.title')} />}
        <div className={styles.title}>
          <Avatar name={m.name} decorative />
          <Heading level={2} size="xl">
            {m.name}
          </Heading>
        </div>
      </Stack>
      <Section title={t('members.detail.info')}>
        <KeyValue
          items={[
            { key: 'firstName', label: t('profile.firstName'), value: m.first_name || null },
            { key: 'lastName', label: t('profile.lastName'), value: m.last_name },
            {
              key: 'email',
              label: t('profile.email'),
              value: <a href={`mailto:${m.email}`}>{m.email}</a>,
            },
            { key: 'role', label: t('members.roleLabel'), value: m.role.name },
            { key: 'joined', label: t('members.detail.since'), value: formatDate(m.joined_at) },
            {
              key: 'lastSeen',
              label: t('members.detail.lastSeen'),
              value: m.last_seen_at ? formatDateTime(m.last_seen_at) : null,
            },
          ]}
        />
      </Section>
      {sales && <MemberSales userId={m.user_id} />}
    </Stack>
  );
}

function MemberMenu({ member }: { member: Member }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const members = useMembers();
  const owners = (members.data ?? []).filter((x) => x.role.owner).length;
  const actions = useMemberActions(member, {
    soleOwner: member.role.owner && owners <= 1,
    onRemoved: () => navigate(LIST, { replace: true }),
  });
  if (actions.items.length === 0) return null;
  return (
    <>
      <PageActions>
        <MoreMenu label={t('common.moreActions')} align="end" items={actions.items} />
      </PageActions>
      {actions.dialogs}
    </>
  );
}

/** Figures and list of the sales a member referred. */
function MemberSales({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const summary = useSalesSummary(userId);
  // Paged: the server sorts (#188).
  const salesSort = useLocalColumnSort();
  const sales = useSales({ referred_by_id: userId, sort: salesSort.value || undefined });
  const rows = sales.data?.pages.flatMap((p) => p.items) ?? [];
  const s = summary.data;
  return (
    <>
      {summary.error && <Alert tone="danger">{errorMessage(summary.error)}</Alert>}
      {s && (
        <div className={styles.stats}>
          <StatCard
            label={t('members.detail.revenueYear', { year: s.year })}
            value={s.year_cents}
            format={formatEuro}
            footnote={t('members.detail.salesCount', { count: s.year_sales })}
          />
          <StatCard
            label={t('members.detail.revenueTotal')}
            value={s.total_cents}
            format={formatEuro}
            footnote={t('members.detail.salesCount', { count: s.sales })}
          />
        </div>
      )}
      <Section title={t('members.detail.sales')} variant="flush">
        <SalesTable
          sort={salesSort.sort}
          onSortChange={salesSort.onSortChange}
          presorted
          caption={t('members.detail.sales')}
          rows={rows}
          loading={sales.isPending}
          hideReferrer
          empty={<EmptyState title={t('members.detail.noSales')} />}
        />
        {sales.hasNextPage && (
          <div className={styles.more}>
            <Button
              variant="secondary"
              loading={sales.isFetchingNextPage}
              onClick={() => void sales.fetchNextPage()}
            >
              {t('sales.list.more')}
            </Button>
          </div>
        )}
      </Section>
    </>
  );
}
